import React from "react"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { getAuth, signOut } from 'firebase/auth'; // Keep getAuth for signOut
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { useFirebase } from '@/context/FirebaseProvider'; // Changed from useDataUtils
import SyncStatusIndicator from '@/components/SyncStatusIndicator'; // SyncStatusIndicator was not used, but keeping import if intended
import { FEATURE_FLAGS } from '@/config/featureFlag';


interface NavBarUserMenuProps {
  isBusinessPage?: boolean
  handleEditCampaign?: () => void
  navigate?: any
}

export function NavBarUserMenu({
  isBusinessPage,
  handleEditCampaign,
  navigate: navigationProp,
}: NavBarUserMenuProps) {
  const auth = getAuth()
  const navigateHook = useNavigate()
  const { toast } = useToast()
  const { firebaseUser } = useFirebase(); // Get firebaseUser
  const user = firebaseUser; // Use firebaseUser from FirebaseProvider
  // const { updateCurrentUser } = useDataUtils(); // Removed

  // Use the navigate prop if provided, otherwise use the hook
  const navigate = navigationProp || navigateHook

  const handleSignOut = async () => {
    try {
      await signOut(auth) // auth is still from getAuth()
      // updateCurrentUser(null) is no longer needed; FirebaseProvider handles this.

      toast({
        title: "Signed out",
        description: "You have been successfully signed out.",
      })
      navigate("/login")
    } catch (error) {
      console.error("Error signing out:", error)
      toast({
        title: "Error",
        description: "Failed to sign out. Please try again.",
        variant: "destructive",
      })
    }
  }

  const handleNavigateToSettings = () => {
    if (FEATURE_FLAGS.SETTINGS) {
      // Change navigation path to match the route in index.tsx
      navigate('/settings')
    }
  }

  const getInitials = () => {
    if (user?.displayName) {
      return user.displayName
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
    }
    return user?.email?.substring(0, 2).toUpperCase() || "U"
  }

  return (
    <div className="flex items-center gap-4">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Avatar className="h-8 w-8 cursor-pointer">
            <AvatarImage src={user?.photoURL || ""} alt="User" />
            <AvatarFallback className="bg-primary text-primary-foreground">
              {getInitials()}
            </AvatarFallback>
          </Avatar>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel>
            {user?.displayName || user?.email || "My Account"}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {FEATURE_FLAGS.SETTINGS && (
            <DropdownMenuItem onClick={(e) => {
              e.preventDefault()
              handleNavigateToSettings()
            }}>
              User Settings
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={(e) => {
            e.preventDefault()
            handleSignOut()
          }}>
            Sign out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}

export default NavBarUserMenu
